import React from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FaCalculator, FaCreditCard, FaHandHoldingUsd, FaMoneyBill } from 'react-icons/fa';
import { formatPrice } from '../../../../utils/priceUtils';
import { FormPagamentosData } from '../../../FormPagamentos/types';

interface DetailsPaymentsBookingProps {
  pagamentosData?: FormPagamentosData;
  valorTotal?: number;
  valorPago?: number;
  statusPagamento?: 'pago' | 'pendente' | 'parcial';
  retiradaDescontoPercentual?: number;
  retiradaDescontoValor?: number;
}

const DetailsPaymentsBooking: React.FC<DetailsPaymentsBookingProps> = ({
  pagamentosData,
  valorTotal = 0,
  valorPago = 0,
  statusPagamento = 'pendente',
  retiradaDescontoPercentual = 0,
  retiradaDescontoValor = 0
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string; icon: React.ReactNode } } = {
      'pago': { variant: 'success', text: 'Pago', icon: <FaHandHoldingUsd /> },
      'pendente': { variant: 'warning', text: 'Pendente', icon: <FaCreditCard /> },
      'parcial': { variant: 'info', text: 'Parcial', icon: <FaMoneyBill /> }
    };
    return statusConfig[status] || statusConfig['pendente'];
  };

  const getFormaPagamentoLabel = (forma: string) => {
    const formas: { [key: string]: string } = {
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'cartao_debito': 'Cartão Débito',
      'cartao_credito': 'Cartão Crédito',
      'transferencia': 'Transferência'
    };
    return formas[forma] || forma;
  };

  const statusInfo = getStatusBadge(statusPagamento);

  // Cálculos para exibição clara
  const descontoEscopo = pagamentosData?.descontoEscopo || 'total';
  const totalBase = pagamentosData?.valorTotal || 0;
  const valorDesconto = pagamentosData
    ? (descontoEscopo === 'total'
        ? totalBase - (pagamentosData.valorComDesconto || totalBase)
        : (pagamentosData.valorEntrada || 0) - (pagamentosData.valorEntradaComDesconto || pagamentosData.valorEntrada || 0))
    : 0;

  const valorTotalLiquido = pagamentosData
    ? (descontoEscopo === 'total'
        ? (pagamentosData.valorComDesconto || totalBase)
        : totalBase)
    : 0;

  const valorEntradaExibida = pagamentosData
    ? (descontoEscopo === 'entrada'
        ? (pagamentosData.valorEntradaComDesconto || pagamentosData.valorEntrada || 0)
        : (pagamentosData.valorEntrada || 0))
    : 0;

  const valorTotalPago = pagamentosData ? (valorEntradaExibida + (pagamentosData.valorFormaPagamento2 || 0)) : 0;

  // Informações da 2ª forma com desconto próprio
  const valor2Bruto = pagamentosData?.valorFormaPagamento2 || 0;
  const percDesc2 = pagamentosData?.descontoPercentualForma2 || 0;
  const valor2ComDesc = pagamentosData?.valorFormaPagamento2ComDesconto ?? valor2Bruto;
  const valorDesc2Forma = Math.max(0, valor2Bruto - valor2ComDesc);
  const totalPagoEntradaLiquido = valorEntradaExibida + valor2ComDesc;

  // Calcular pagamento de retirada
  // CORRIGIR AQUI: usar valorTotalPago ao invés de apenas valorEntrada
  const valorEntradaPaga = totalPagoEntradaLiquido; // usar líquido efetivamente pago na entrada
  // Evitar valores negativos quando ainda não houve retirada
  const valorRetiradaPago = Math.max(0, valorPago - valorEntradaPaga);
  const saldoDevedorAposEntrada = pagamentosData?.saldoDevedor || 0;
  // Saldo atual considera pagamento de retirada + desconto aplicado na retirada
  const saldoDevedorAtual = Math.max(0, saldoDevedorAposEntrada - valorRetiradaPago - (retiradaDescontoValor || 0));
  const haPagamentoRetirada = valorRetiradaPago > 0 || (retiradaDescontoValor || 0) > 0;

  return (
    <Card className="mb-3 mt-3 shadow-sm">
      <Card.Body className="p-2 p-md-3">
        {pagamentosData ? (
          <>
            <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#d6ecff' }}>
              <h6 className="mb-3">
                <FaMoneyBill size={20} /> Detalhamento do Pagamento de Entrada
              </h6>

              {/* Detalhamento do Pagamento de Entrada */}
              <Row>
                <Col md={12}>
                  <div className="mt-3 p-3 bg-white rounded border">
                    <h6 className="text-muted mb-3">
                      <FaCalculator className="me-2" />
                      Resumo do Pagamento de Entrada
                    </h6>
                    <div className="row g-2 small">
                      <div className="col-12">
                        <div className="d-flex justify-content-between">
                          <span>Valor Total:</span>
                          <span className="fw-semibold">{formatPrice(pagamentosData.valorTotal)}</span>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="d-flex justify-content-between">
                          <span>{descontoEscopo === 'total' ? 'Desconto no Total' : 'Desconto na Entrada'} ({pagamentosData.descontoPercentual}%):</span>
                          <span>- {formatPrice(valorDesconto)}</span>
                        </div>
                      </div>
                      <div className="col-12 border-top pt-2">
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold">Valor Líquido:</span>
                          <span className="fw-bold">{formatPrice(valorTotalLiquido)}</span>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="d-flex justify-content-between">
                          <span>1ª Forma ({getFormaPagamentoLabel(pagamentosData.formaPagamentoEntrada)}):</span>
                          <span>- {formatPrice(valorEntradaExibida)}</span>
                        </div>
                      </div>
                      {pagamentosData.usaDuasFormas && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>2ª Forma ({getFormaPagamentoLabel(pagamentosData.formaPagamento2)}):</span>
                            <span>- {formatPrice(pagamentosData.valorFormaPagamento2 || 0)}</span>
                          </div>
                        </div>
                      )}
                      {pagamentosData.usaDuasFormas && percDesc2 > 0 && (
                        <>
                          <div className="col-12">
                            <div className="d-flex justify-content-between">
                              <span>Desconto 2ª Forma ({percDesc2}%):</span>
                              <span className="text-danger">- {formatPrice(valorDesc2Forma)}</span>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">Valor cobrado na 2ª forma:</span>
                              <span className="fw-bold">{formatPrice(valor2ComDesc)}</span>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="col-12 border-top pt-2">
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold">Total Pago (Entrada):</span>
                          <span className="fw-bold text-success">{formatPrice(totalPagoEntradaLiquido)}</span>
                        </div>
                      </div>
                      
                      <div className="col-12 border-top pt-2">
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">Saldo Devedor (Após Entrada):</span>
                          <span className="fw-bold text-danger">{formatPrice(pagamentosData.saldoDevedor)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            {/* Seção de Pagamento de Retirada - Nova */}
            {haPagamentoRetirada && (
              <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#fff3cd' }}>
                <h6 className="mb-3">
                  <FaMoneyBill size={20} /> Detalhamento do Pagamento de Retirada
                </h6>

                <Row>
                  <Col md={12}>
                    <div className="mt-3 p-3 bg-white rounded border">
                      <h6 className="text-muted mb-3">
                        <FaCalculator className="me-2" />
                        Resumo de Pagamento Final 
                      </h6>
                      <div className="row g-2 small">
                        <div className="col-12">
                          <div className="d-flex justify-content-between">
                            <span>✓ Entrada já paga:</span>
                            <span className="fw-semibold text-success">{formatPrice(valorEntradaPaga)}</span>
                          </div>
                        </div>
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span >✓ Pagamento Final:</span>
                            <span className="fw-semibold text-info">{formatPrice(valorRetiradaPago)}</span>
                          </div>
                        </div>
                        {retiradaDescontoValor > 0 && (
                          <div className="col-12 border-top pt-2">
                            <div className="d-flex justify-content-between">
                              <span className="fw-semibold">Desconto na Retirada ({retiradaDescontoPercentual.toFixed(2)}%):</span>
                              <span className="fw-bold text-danger">- {formatPrice(retiradaDescontoValor)}</span>
                            </div>
                          </div>
                        )}
                        <div className="col-12 border-top pt-2">
                          <div className="d-flex justify-content-between">
                            <span className="fw-semibold">Total Pago:</span>
                            <span className="fw-bold text-success fs-6">{formatPrice(valorPago)}</span>
                          </div>
                        </div>
                        {saldoDevedorAtual > 0 ? (
                          <div className="col-12 border-top pt-2">
                            <div className="d-flex justify-content-between">
                              <span className="fw-bold">Saldo Devedor:</span>
                              <span className="fw-bold text-danger fs-6">{formatPrice(saldoDevedorAtual)}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="col-12 border-top pt-2">
                            <div className="d-flex justify-content-between">
                              <span className="fw-bold">Status:</span>
                              <span className="badge bg-success">Totalmente Pago</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </>
        ) : (
          <span className="text-muted">Não há dados de pagamento</span>
        )}
      </Card.Body>
    </Card>
  );
};

export default DetailsPaymentsBooking;